import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, ContentCopy, Delete, Edit, Facebook, Instagram, Refresh } from "@mui/icons-material";
import { toast } from "react-toastify";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

function platformLabel(platform) {
  return platform === "facebook" ? "Facebook" : "Instagram";
}

function connectionIdValue(platform, row) {
  if (!row) return "";
  return platform === "facebook" ? String(row.pageId || "") : String(row.instagramAccountId || "");
}

function safeDate(value, locale) {
  if (!value) return "-";
  try {
    return new Date(String(value)).toLocaleString(locale || undefined);
  } catch (_) {
    return String(value);
  }
}

export default function FacebookInstagram({ onlyPlatform }) {
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const normalizedOnlyPlatform = String(onlyPlatform || "").trim().toLowerCase();
  const platformLocked = normalizedOnlyPlatform === "facebook" || normalizedOnlyPlatform === "instagram";

  const [loading, setLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaConfig, setMetaConfig] = useState(null);
  const [accounts, setAccounts] = useState([]);

  const [filters, setFilters] = useState({
    q: "",
    category: "",
    dateFrom: "",
    dateTo: "",
    showInactive: true,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(platformLocked ? normalizedOnlyPlatform : "");
  const [form, setForm] = useState({
    accountName: "",
    externalId: "",
    category: "",
    accessToken: "",
    isActive: true,
  });

  const getIdLabel = (platform) =>
    platform === "facebook"
      ? i18n.t("facebookInstagram.labels.pageId")
      : i18n.t("facebookInstagram.labels.instagramAccountId");

  const title = platformLocked ? platformLabel(normalizedOnlyPlatform) : i18n.t("facebookInstagram.titleAll");

  const handleCopy = async (text) => {
    const value = String(text || "");
    if (!value.trim()) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(i18n.t("facebookInstagram.toasts.copied"));
    } catch (_) {
      toast.info(value);
    }
  };

  const handlePlanLimitIfAny = (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 429 && detail?.error === "plan_limit_exceeded") {
      toast.error(i18n.t("backendErrors.plan_limit_exceeded"));
      history.push("/subscription");
      return true;
    }
    return false;
  };

  const loadMetaConfig = async () => {
    try {
      setMetaLoading(true);
      const { data } = await api.get("/facebook-instagram/meta-config");
      setMetaConfig(data || null);
    } catch (error) {
      console.error("Error fetching meta config:", error);
      setMetaConfig(null);
    } finally {
      setMetaLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const channels = platformLocked ? [normalizedOnlyPlatform] : ["facebook", "instagram"];
      const { data } = await api.get("/channel-connections", {
        params: { channels: JSON.stringify(channels) },
      });

      if (!Array.isArray(data)) {
        setAccounts([]);
        return;
      }

      setAccounts(
        data.map((ch) => {
          const cfg = ch?.config && typeof ch.config === "object" ? ch.config : {};
          return {
            id: ch.id,
            platform: ch.channel,
            name: ch.accountName || ch.externalAccountId || ch.channel,
            pageId: ch.pageId || "",
            instagramAccountId: ch.instagramAccountId || "",
            status: ch.status || "disconnected",
            lastSyncAt: ch.lastSyncAt || "",
            isActive: Boolean(ch.isActive),
            webhookVerified: Boolean(ch.webhookVerified),
            webhookVerifiedAt: ch.webhookVerifiedAt || "",
            createdAt: ch.createdAt || "",
            category: String(cfg?.category || "").trim(),
          };
        })
      );
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
    loadMetaConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredAccounts = useMemo(() => {
    const q = String(filters.q || "").trim().toLowerCase();
    const category = String(filters.category || "").trim().toLowerCase();
    const dateFrom = String(filters.dateFrom || "").trim();
    const dateTo = String(filters.dateTo || "").trim();
    const showInactive = Boolean(filters.showInactive);

    const fromMs = dateFrom ? Date.parse(`${dateFrom}T00:00:00`) : null;
    const toMs = dateTo ? Date.parse(`${dateTo}T23:59:59`) : null;

    return (accounts || []).filter((a) => {
      if (!showInactive && !a?.isActive) return false;

      if (q) {
        const idValue = connectionIdValue(a?.platform, a);
        const haystack = `${String(a?.name || "")} ${String(idValue || "")}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      if (category) {
        const c = String(a?.category || "").trim().toLowerCase();
        if (!c.includes(category)) return false;
      }

      if (fromMs || toMs) {
        const createdMs = a?.createdAt ? Date.parse(String(a.createdAt)) : NaN;
        if (!Number.isFinite(createdMs)) return false;
        if (fromMs && createdMs < fromMs) return false;
        if (toMs && createdMs > toMs) return false;
      }

      return true;
    });
  }, [accounts, filters]);

  const openDialog = (platform, initial) => {
    const p = String(platform || "").trim().toLowerCase();
    setSelectedPlatform(p);
    setEditing(initial || null);
    setForm({
      accountName: String(initial?.name || ""),
      externalId: connectionIdValue(p, initial),
      category: String(initial?.category || ""),
      accessToken: "",
      isActive: initial?.isActive !== undefined ? Boolean(initial.isActive) : true,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSaving(false);
    setEditing(null);
    setForm({
      accountName: "",
      externalId: "",
      category: "",
      accessToken: "",
      isActive: true,
    });
    if (!platformLocked) {
      setSelectedPlatform("");
    }
  };

  const handleToggleActive = async (account) => {
    try {
      const next = !Boolean(account?.isActive);
      await api.put(`/channel-connections/${account?.id}`, { isActive: next });
      toast.success(
        next
          ? i18n.t("facebookInstagram.toasts.accountActivated")
          : i18n.t("facebookInstagram.toasts.accountDeactivated")
      );
      fetchAccounts();
    } catch (error) {
      console.error("Error toggling account:", error);
      if (handlePlanLimitIfAny(error)) return;
      toast.error(i18n.t("facebookInstagram.toasts.statusToggleError"));
    }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm(i18n.t("facebookInstagram.confirm.disconnect"))) return;
    try {
      await api.delete(`/channel-connections/${accountId}`);
      toast.success(i18n.t("facebookInstagram.toasts.disconnectSuccess"));
      fetchAccounts();
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error(i18n.t("facebookInstagram.toasts.disconnectError"));
    }
  };

  const handleSave = async () => {
    const platform = String(selectedPlatform || "").trim().toLowerCase();
    const name = String(form.accountName || "").trim();
    const externalId = String(form.externalId || "").trim();
    const category = String(form.category || "").trim();
    const accessToken = String(form.accessToken || "").trim();
    const isActive = Boolean(form.isActive);

    if (platform !== "facebook" && platform !== "instagram") {
      toast.error(i18n.t("facebookInstagram.validations.selectValidChannel"));
      return;
    }
    if (!name) {
      toast.error(i18n.t("facebookInstagram.validations.nameRequired"));
      return;
    }
    if (!externalId) {
      toast.error(i18n.t("facebookInstagram.validations.idRequired", { idLabel: getIdLabel(platform) }));
      return;
    }
    if (!editing?.id && !accessToken) {
      toast.error(i18n.t("facebookInstagram.validations.accessTokenRequired"));
      return;
    }

    const payload = {
      channel: platform,
      accountName: name,
      isActive,
      config: { category: category || null },
    };
    if (platform === "facebook") {
      payload.pageId = externalId;
    } else {
      payload.instagramAccountId = externalId;
    }
    if (accessToken) {
      payload.accessToken = accessToken;
    }

    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/channel-connections/${editing.id}`, payload);
        toast.success(i18n.t("facebookInstagram.toasts.connectionUpdated"));
      } else {
        await api.post("/channel-connections", payload);
        toast.success(i18n.t("facebookInstagram.toasts.connectionCreated"));
      }
      closeDialog();
      fetchAccounts();
    } catch (error) {
      console.error("Error saving connection:", error);
      if (handlePlanLimitIfAny(error)) return;
      toast.error(i18n.t("facebookInstagram.toasts.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      sx={{
        p: 3,
        minHeight: "calc(100vh - 100px)",
        background: "linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)",
      }}
      elevation={0}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {title}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {i18n.t("facebookInstagram.subtitle")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchAccounts();
              loadMetaConfig();
            }}
            disabled={loading || metaLoading}
          >
            {i18n.t("common.refresh")}
          </Button>

          {platformLocked ? (
            <Button variant="contained" startIcon={<Add />} onClick={() => openDialog(normalizedOnlyPlatform, null)}>
              {i18n.t("facebookInstagram.actions.newConnection")}
            </Button>
          ) : (
            <>
              <Button variant="contained" startIcon={<Add />} onClick={() => openDialog("facebook", null)}>
                {i18n.t("facebookInstagram.actions.connectFacebook")}
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={() => openDialog("instagram", null)}>
                {i18n.t("facebookInstagram.actions.connectInstagram")}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Card sx={{ mb: 4, border: "1px solid #e5e7eb" }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {i18n.t("facebookInstagram.meta.title")}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadMetaConfig}
              disabled={metaLoading}
            >
              {metaLoading ? i18n.t("common.loading") : i18n.t("common.refresh")}
            </Button>
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {i18n.t("facebookInstagram.meta.step1")}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("facebookInstagram.meta.webhookUrl")}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {metaConfig?.webhookUrl || "/facebook-instagram/webhook/{webhookKey}"}
            </Typography>
            <IconButton
              size="small"
              title={i18n.t("facebookInstagram.meta.copy")}
              onClick={() => handleCopy(metaConfig?.webhookUrl)}
              disabled={!metaConfig?.webhookUrl}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("facebookInstagram.meta.verifyToken")}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {metaConfig?.verifyToken || "-"}
            </Typography>
            <IconButton
              size="small"
              title={i18n.t("facebookInstagram.meta.copy")}
              onClick={() => handleCopy(metaConfig?.verifyToken)}
              disabled={!metaConfig?.verifyToken}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            {i18n.t("facebookInstagram.meta.step2")}
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 0, mb: 2 }}>
            {(!platformLocked || normalizedOnlyPlatform === "facebook") && (
              <li>
                <Typography variant="body2">
                  <b>Facebook</b>: {i18n.t("facebookInstagram.meta.objectWord")} <b>page</b>,{" "}
                  {i18n.t("facebookInstagram.meta.fieldsWord")}{" "}
                  <span style={{ fontFamily: "monospace" }}>
                    {(metaConfig?.facebook?.subscribedFields || ["messages"]).join(", ")}
                  </span>
                </Typography>
              </li>
            )}
            {(!platformLocked || normalizedOnlyPlatform === "instagram") && (
              <li>
                <Typography variant="body2">
                  <b>Instagram</b>: {i18n.t("facebookInstagram.meta.objectWord")} <b>instagram</b>,{" "}
                  {i18n.t("facebookInstagram.meta.fieldsWord")}{" "}
                  <span style={{ fontFamily: "monospace" }}>
                    {(metaConfig?.instagram?.subscribedFields || ["messages"]).join(", ")}
                  </span>
                </Typography>
              </li>
            )}
          </Box>

          <Typography variant="body2" color="textSecondary">
            {i18n.t("facebookInstagram.meta.step3")}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
            {i18n.t("facebookInstagram.meta.user")} {user?.email || "-"}
          </Typography>
        </CardContent>
      </Card>

      {loading ? (
        <LinearProgress sx={{ mb: 3 }} />
      ) : (
        <>
          <Card sx={{ mb: 3, border: "1px solid #e5e7eb" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {i18n.t("facebookInstagram.filters.title")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={i18n.t("facebookInstagram.filters.searchLabel")}
                    value={filters.q}
                    onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={i18n.t("facebookInstagram.filters.category")}
                    value={filters.category}
                    onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label={i18n.t("facebookInstagram.filters.from")}
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters((p) => ({ ...p, dateFrom: e.target.value }))}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label={i18n.t("facebookInstagram.filters.to")}
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters((p) => ({ ...p, dateTo: e.target.value }))}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filters.showInactive}
                        onChange={(e) => setFilters((p) => ({ ...p, showInactive: e.target.checked }))}
                      />
                    }
                    label={i18n.t("facebookInstagram.filters.inactiveShort")}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {i18n.t("facebookInstagram.table.title")}
              </Typography>

              {filteredAccounts.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {i18n.t("facebookInstagram.table.empty")}
                </Alert>
              ) : (
                <TableContainer sx={{ mt: 2 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                      <TableRow>
                        {!platformLocked && (
                          <TableCell>
                            <strong>{i18n.t("facebookInstagram.table.headers.platform")}</strong>
                          </TableCell>
                        )}
                        <TableCell>
                          <strong>{i18n.t("facebookInstagram.table.headers.name")}</strong>
                        </TableCell>
                        <TableCell>
                          <strong>{platformLocked ? getIdLabel(normalizedOnlyPlatform) : i18n.t("facebookInstagram.table.headers.id")}</strong>
                        </TableCell>
                        <TableCell>
                          <strong>{i18n.t("facebookInstagram.table.headers.category")}</strong>
                        </TableCell>
                        <TableCell>
                          <strong>{i18n.t("facebookInstagram.table.headers.status")}</strong>
                        </TableCell>
                        <TableCell>
                          <strong>{i18n.t("facebookInstagram.table.headers.webhook")}</strong>
                        </TableCell>
                        <TableCell>
                          <strong>{i18n.t("facebookInstagram.table.headers.created")}</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>{i18n.t("facebookInstagram.table.headers.active")}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{i18n.t("facebookInstagram.table.headers.actions")}</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAccounts.map((account) => (
                        <TableRow key={account.id} hover>
                          {!platformLocked && (
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Avatar
                                  sx={{
                                    bgcolor: account.platform === "facebook" ? "#1877f2" : "#e1306c",
                                    mr: 2,
                                  }}
                                >
                                  {account.platform === "facebook" ? <Facebook /> : <Instagram />}
                                </Avatar>
                                <Typography variant="body2" fontWeight={600}>
                                  {platformLabel(account.platform)}
                                </Typography>
                              </Box>
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {account.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                              {connectionIdValue(account.platform, account) || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>{account.category || "-"}</TableCell>
                          <TableCell>
                            <Chip
                              label={
                                account.status === "connected"
                                  ? i18n.t("facebookInstagram.status.connected")
                                  : account.status === "error"
                                  ? i18n.t("facebookInstagram.status.error")
                                  : i18n.t("facebookInstagram.status.disconnected")
                              }
                              size="small"
                              color={
                                account.status === "connected"
                                  ? "success"
                                  : account.status === "error"
                                  ? "warning"
                                  : "default"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                account.webhookVerified
                                  ? i18n.t("facebookInstagram.status.verified")
                                  : i18n.t("facebookInstagram.status.pending")
                              }
                              size="small"
                              color={account.webhookVerified ? "success" : "default"}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{safeDate(account.createdAt, i18n.language)}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Switch checked={account.isActive} onChange={() => handleToggleActive(account)} />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openDialog(account.platform, account)}
                              title={i18n.t("facebookInstagram.tooltips.edit")}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(account.id)}
                              title={i18n.t("facebookInstagram.tooltips.disconnect")}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing?.id
            ? i18n.t("facebookInstagram.dialog.editTitle", { platform: selectedPlatform ? platformLabel(selectedPlatform) : "" })
            : i18n.t("facebookInstagram.dialog.newTitle", { platform: selectedPlatform ? platformLabel(selectedPlatform) : "" })}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            {i18n.t("facebookInstagram.dialog.info")}
          </Alert>

          <TextField
            label={i18n.t("facebookInstagram.dialog.fields.name")}
            value={form.accountName}
            onChange={(e) => setForm((p) => ({ ...p, accountName: e.target.value }))}
            fullWidth
            margin="dense"
          />

          <TextField
            label={getIdLabel(selectedPlatform)}
            value={form.externalId}
            onChange={(e) => setForm((p) => ({ ...p, externalId: e.target.value }))}
            fullWidth
            margin="dense"
            sx={{ fontFamily: "monospace" }}
          />

          <TextField
            label={i18n.t("facebookInstagram.dialog.fields.categoryOptional")}
            value={form.category}
            onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
            fullWidth
            margin="dense"
          />

          <TextField
            label={
              editing?.id
                ? i18n.t("facebookInstagram.dialog.fields.accessTokenEditHint")
                : i18n.t("facebookInstagram.dialog.fields.accessToken")
            }
            value={form.accessToken}
            onChange={(e) => setForm((p) => ({ ...p, accessToken: e.target.value }))}
            fullWidth
            margin="dense"
            type="password"
          />

          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.isActive)}
                onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
              />
            }
            label={i18n.t("common.active")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            {i18n.t("common.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              background:
                selectedPlatform === "facebook"
                  ? "linear-gradient(135deg, #1877f2 0%, #0d5dbf 100%)"
                  : "linear-gradient(135deg, #e1306c 0%, #c13584 100%)",
            }}
            disabled={saving}
          >
            {saving ? i18n.t("facebookInstagram.dialog.saving") : i18n.t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
